'use client'

import { useState, useEffect } from 'react'
import { GlobalSidebar } from '@/components/ui/global-sidebar'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Filter, X, User as UserIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { Skeleton } from '@/components/ui/skeleton'
import { ScrollArea } from '@/components/ui/scroll-area'

export interface NewsFilterState {
  authors: number[]
}

interface User {
  id: number
  name: string
}

interface NewsFilterSidebarProps {
  isOpen: boolean
  onClose: () => void
  filters: NewsFilterState
  onFiltersChange: (filters: NewsFilterState) => void
  projectId?: string
  getAuthHeaders: () => Record<string, string>
}

export function NewsFilterSidebar({
  isOpen,
  onClose,
  filters,
  onFiltersChange,
  projectId,
  getAuthHeaders
}: NewsFilterSidebarProps) {
  const t = useTranslations('newsPage')
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isOpen) {
      fetchUsers()
    }
  }, [isOpen, projectId])

  const fetchUsers = async () => {
    setLoading(true)
    try {
      const headers = getAuthHeaders()

      // Collect unique authors directly from news articles (with names)
      const authorsMap = new Map<number, User>()
      let offset = 0
      const limit = 100
      let totalFetched = 0

      // Fetch ALL news articles to get complete author list
      while (true) {
        const projectParam = projectId && projectId !== 'all' ? `&project_id=${projectId}` : ''
        const cacheBuster = `&_cb=${Date.now()}`
        const response = await fetch(`/api/news?offset=${offset}&limit=${limit}${projectParam}${cacheBuster}`, {
          headers,
          cache: 'no-store',
          next: { revalidate: 0 }
        })

        if (!response.ok) break

        const data = await response.json()
        const news = data.news || []
        const totalCount = data.total_count || 0

        if (news.length === 0) break

        // Collect unique authors with their names directly from the news response
        news.forEach((article: any) => {
          if (article.author?.id && article.author?.name && !authorsMap.has(article.author.id)) {
            authorsMap.set(article.author.id, {
              id: article.author.id,
              name: article.author.name
            })
          }
        })

        totalFetched += news.length

        // Check if we've fetched all articles
        if (totalFetched >= totalCount || news.length < limit) {
          break
        }

        offset += limit

        // Safety limit to prevent infinite loops
        if (offset > 10000) break
      }

      // Convert map to array and sort alphabetically
      const validUsers = Array.from(authorsMap.values())
      validUsers.sort((a, b) => a.name.localeCompare(b.name))

      setUsers(validUsers)
    } catch (error) {
      console.error('Error fetching users:', error)
      setUsers([])
    } finally {
      setLoading(false)
    }
  }

  const handleAuthorToggle = (userId: number) => {
    const newAuthors = filters.authors.includes(userId)
      ? filters.authors.filter(id => id !== userId)
      : [...filters.authors, userId]

    onFiltersChange({
      ...filters,
      authors: newAuthors
    })
  }

  const handleClearAll = () => {
    onFiltersChange({
      authors: []
    })
  }

  const getActiveFilterCount = () => {
    return filters.authors.length
  }

  return (
    <GlobalSidebar
      isOpen={isOpen}
      onClose={onClose}
      title={t('filters')}
      subtitle={t('filterNewsArticles')}
      icon={<Filter className="h-5 w-5" />}
      footer={
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleClearAll}
            disabled={getActiveFilterCount() === 0}
            className="flex-1"
          >
            <X className="h-4 w-4 mr-2" />
            {t('clearAll')}
          </Button>
          <Button
            onClick={onClose}
            className="flex-1"
          >
            {t('apply')}
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Active Filters Count */}
        {getActiveFilterCount() > 0 && (
          <div className="flex items-center justify-between p-3 bg-primary/10 rounded-lg">
            <span className="text-sm font-medium">{t('activeFilters')}</span>
            <Badge variant="default">{getActiveFilterCount()}</Badge>
          </div>
        )}

        {/* Authors Filter */}
        <div>
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-foreground mb-1">{t('filterByAuthor')}</h3>
            <p className="text-xs text-muted-foreground">
              Select authors to filter news articles
            </p>
          </div>

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="h-4 w-4 rounded" />
                  <Skeleton className="h-4 flex-1 rounded-full" />
                </div>
              ))}
            </div>
          ) : users.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-8 border border-dashed rounded-lg">
              {t('noAuthorsFound')}
            </div>
          ) : (
            <>
              <div className="mb-2 text-xs text-muted-foreground">
                {users.length} {users.length === 1 ? 'author' : 'authors'} available
                {filters.authors.length > 0 && ` • ${filters.authors.length} selected`}
              </div>
              <ScrollArea className="h-[400px] -mr-4 pr-4">
                <div className="space-y-1">
                  {users.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-accent cursor-pointer transition-colors group"
                      onClick={() => handleAuthorToggle(user.id)}
                    >
                      <Checkbox
                        id={`author-${user.id}`}
                        checked={filters.authors.includes(user.id)}
                        onCheckedChange={() => handleAuthorToggle(user.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="data-[state=unchecked]:bg-transparent data-[state=unchecked]:border-2"
                      />
                      <label
                        htmlFor={`author-${user.id}`}
                        className="text-sm cursor-pointer flex-1 group-hover:text-foreground"
                      >
                        {user.name}
                      </label>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </>
          )}
        </div>
      </div>
    </GlobalSidebar>
  )
}
